import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useImageCarousel, useTranslations } from '../hooks'
import { Slide, SliderButton } from '.'
import ErrorBoundary from '../utils/error-boundary'
import Icon from '../icon'

type Props = {
  imageIds: string[]
  slugText: string
  setShowModal: (active: number) => void
  initialSlide?: number
  fullScreen?: boolean
  imageSliderProps?: React.CSSProperties
  sizes?: string
}

export const ImageCarousel = ({
  imageIds,
  slugText,
  setShowModal,
  initialSlide,
  fullScreen = false,
  imageSliderProps,
  sizes,
}: Props) => {
  const numberOfImages = imageIds.length
  const [swipingVertical, setSwipingVertical] = useState(false)
  const carouselRef = useRef<HTMLElement>(null)
  const prevSwipeYRef = useRef<number | null>(null)
  const prevSwipeXRef = useRef<number | null>(null)
  const { translate } = useTranslations()
  const {
    currentSlide,
    setCurrentSlide,
    nextSlide,
    prevSlide,
    handlers,
    style,
    sliderStyle,
    preventClick,
  } = useImageCarousel(numberOfImages, initialSlide)

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) =>
      handleKeyboardNavigation(e, prevSlide, nextSlide, currentSlide, numberOfImages),
    [currentSlide, numberOfImages]
  )

  useEffect(() => {
    carouselRef.current = document.querySelector('.c-imageslider__images')

    const onKeyUp = (e: KeyboardEvent) => {
      if (document.activeElement === carouselRef.current) {
        handleKeyUp(e)
      }
    }

    document.addEventListener('keyup', onKeyUp)
    return () => document.removeEventListener('keyup', onKeyUp)
  }, [handleKeyUp])

  useEffect(() => {
    if (swipingVertical) {
      const timeout = setTimeout(() => {
        setSwipingVertical(false)
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [swipingVertical])

  return (
    <ErrorBoundary>
      {numberOfImages > 0 && (
        <div
          className={classNames('c-sticky-slider-container', {
            'c-sticky-slider-container--fullScreen': fullScreen,
          })}>
          <div
            className="c-imageslider"
            style={imageSliderProps}
            onTouchMove={e =>
              handleOnTouchMove(setSwipingVertical, prevSwipeXRef, prevSwipeYRef, e)
            }>
            {/* ARIA live region for slide change announcements */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="c-imageslider__visually-hidden">
              {`Slide ${currentSlide + 1} of ${numberOfImages}`}
            </div>
            <div
              {...handlers}
              style={style}
              className={classNames('c-imageslider__images', {
                'u-pointer-events-none': swipingVertical,
              })}
              onClick={e => (preventClick ? e.preventDefault() : setShowModal(currentSlide))}
              role="button"
              aria-haspopup="dialog"
              aria-label="Product images gallery carousel"
              tabIndex={0}
              onKeyDown={event => {
                if (event.code === 'Enter') {
                  if (preventClick) {
                    event.preventDefault()
                  } else {
                    setShowModal(currentSlide)
                  }
                }
              }}>
              <ErrorBoundary>
                <Slide
                  imageId={imageIds[imageIds.length - 1]}
                  slugText={slugText}
                  visible={numberOfImages > 1}
                  fullScreen={fullScreen}
                  sizes={sizes}
                />
              </ErrorBoundary>
              {imageIds.map((imageId, i) => (
                <ErrorBoundary key={imageId + i}>
                  <Slide
                    slugText={slugText}
                    imageId={imageId}
                    imagePriority={currentSlide === i}
                    fetchPriority={currentSlide === i ? 'high' : undefined}
                    decoding={currentSlide === i ? 'sync' : 'async'}
                    sizes={sizes}
                    fullScreen={fullScreen}
                  />
                </ErrorBoundary>
              ))}
              <ErrorBoundary>
                <Slide
                  imageId={imageIds[0]}
                  slugText={slugText}
                  visible={numberOfImages > 1}
                  fullScreen={fullScreen}
                  sizes={sizes}
                />
              </ErrorBoundary>
            </div>
            <button
              className="c-imageslider__btn c-imageslider__btn--prev"
              onClick={() => prevSlide(currentSlide, numberOfImages)}
              aria-label={translate('ImageSlider_Previous_Image')}>
              <Icon name="arrow-left-small" />
            </button>
            <button
              className="c-imageslider__btn c-imageslider__btn--next"
              onClick={() => nextSlide(currentSlide, numberOfImages)}
              aria-label={translate('ImageSlider_Next_Image')}>
              <Icon name="arrow-right-small" />
            </button>
          </div>
          {!fullScreen ? (
            <div className="c-imageslider__thumbs d-none d-md-flex d-print-none">
              {imageIds.map((imageId, i) => (
                <SliderButton
                  key={imageId + i}
                  imageId={imageId}
                  slugText={slugText}
                  index={i}
                  isSelected={i === currentSlide}
                  numberOfImages={numberOfImages}
                  select={() => setCurrentSlide(i)}
                />
              ))}
            </div>
          ) : null}
          <div
            className={classNames('c-imageslider__slider', {
              'c-imageslider__slider--fullscreen': fullScreen,
            })}>
            <div className="c-imageslider__slider--active" style={sliderStyle}></div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  )
}

const handleKeyboardNavigation = (
  e: KeyboardEvent,
  prevSlide: (customSlide?: number | undefined, customNumberOfSlides?: number | undefined) => void,
  nextSlide: (customSlide?: number | undefined, customNumberOfSlides?: number | undefined) => void,
  currentSlide: number,
  numberOfImages: number
): void => {
  switch (e.key) {
    case 'ArrowLeft':
      prevSlide(currentSlide, numberOfImages)
      break
    case 'ArrowRight':
      nextSlide(currentSlide, numberOfImages)
      break
    default:
      break
  }
}

const handleOnTouchMove = (
  setSwipingVertical: React.Dispatch<React.SetStateAction<boolean>>,
  prevSwipeXRef: React.MutableRefObject<number | null>,
  prevSwipeYRef: React.MutableRefObject<number | null>,
  e: React.TouchEvent<HTMLDivElement>
): void => {
  const currentY = e.touches[0].clientY
  const currentX = e.touches[0].clientX

  const Ydiff = Math.abs(prevSwipeYRef.current ? currentY - prevSwipeYRef.current : 0)
  const XDiff = Math.abs(prevSwipeXRef.current ? currentX - prevSwipeXRef.current : 0)

  const swipingY = Ydiff > XDiff

  setSwipingVertical(swipingY)

  prevSwipeXRef.current = currentX
  prevSwipeYRef.current = currentY
}

export default ImageCarousel
